<script setup lang="ts">
import { getRandomAvatar } from '@/utils/getRandomAvatar'
import { computed, inject, ref, type Ref } from 'vue'
import { ORG_CARD_WIDTH, ORG_CHILDREN_GAP, ORG_LEVEL_VERTICAL_GAP, measureOrgSubtreeSpanWidth } from '../constants/orgChartLayout'
import type { IStaffDepartment } from '../types/IStaffDepartment'

const props = defineProps<{
  node: IStaffDepartment & { isMo?: boolean }
  selectedDeptId: string | null
  expandedIds: string[]
}>()

const searchQuery = inject<Ref<string>>('searchQuery', ref(''))
const isFullTreeLayout = inject<Ref<boolean>>('orgFullTreeLayout', ref(false))

const orgNodeStyle = computed(() => {
  if (!isFullTreeLayout.value)
    return undefined
  const w = measureOrgSubtreeSpanWidth(props.node, props.expandedIds)
  return { width: `${w}px` }
})

const childrenOffsetLeft = computed(() => {
  const children = (props.node.children || []).filter(c => c && c.id !== 'empty')
  const count = children.length
  if (count <= 0) return 0

  if (isFullTreeLayout.value) {
    const span = measureOrgSubtreeSpanWidth(props.node, props.expandedIds)
    const rowWidth = children.reduce((sum, child, idx) => {
      const w = measureOrgSubtreeSpanWidth(child, props.expandedIds)
      return sum + w + (idx > 0 ? ORG_CHILDREN_GAP : 0)
    }, 0)
    return (span - rowWidth) / 2
  }

  const rowWidth = count * ORG_CARD_WIDTH + (count - 1) * ORG_CHILDREN_GAP
  return (ORG_CARD_WIDTH - rowWidth) / 2
})

const highlightMatch = (text: string, searchTerm: string) => {
  const query = (searchTerm || '').trim().toLowerCase()
  if (!query || !text)
    return text
  const safe = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const re = new RegExp(`(${safe})`, 'gi')

  return text.replace(re, '<mark class="search-highlight">$1</mark>')
}

defineEmits<{
  'select': [id: string, name: string]
  'toggle-expand': [id: string]
}>()

const hasChildren = computed(() => {
  return !!(props.node.children && props.node.children.length > 0)
})

const isExpanded = computed(() => {
  return props.expandedIds.includes(String(props.node.id))
})

const manager = computed(() => {
  return props.node.manager || null
})

const employeeCount = computed(() => {
  return props.node.employee_count ?? 0
})

const fallbackAvatars = new Map<string, string>()

function getAvatarUrl(employee: NonNullable<IStaffDepartment['manager']>): string {
  if (employee.avatar) return employee.avatar

  const id = String(employee.id)
  if (!fallbackAvatars.has(id))
    fallbackAvatars.set(id, getRandomAvatar())

  return fallbackAvatars.get(id)!
}
</script>

<template>
  <div
    class="org-node"
    :class="{ '--expanded': isExpanded }"
    :data-dept-id="String(node.id)"
    :style="orgNodeStyle"
  >
    <div class="org-node__card-wrap">
      <div
        :id="'dept-card-' + node.id"
        class="dept-node-card"
        :class="{
          'selected': selectedDeptId === String(node.id),
          'mo-card': node.isMo || ((node.short_name || node.name) && (node.short_name || node.name).includes('МО'))
        }"
        @click="$emit('select', node.id, node.short_name || node.name)"
      >
        <!-- Шапка -->
        <div class="d-flex align-start px-4 py-3 border-b dept-header w-100 gap-2">
          <div class="icon-wrapper d-flex align-center justify-center rounded mt-0.5 flex-shrink-0">
            <VIcon
              :icon="node.isMo ? 'ri-building-4-line' : 'ri-git-branch-line'"
              :class="node.isMo ? 'accent-color' : 'text-secondary'"
              :size="node.isMo ? 20 : 18"
            />
          </div>
          <div class="dept-title-scroll flex-grow-1 min-width-0">
            <span
              :class="node.isMo ? 'accent-color font-weight-bold' : 'font-weight-bold text-high-emphasis'"
              :style="node.isMo ? 'font-size: 15px; line-height: 1.3;' : 'font-size: 14px; line-height: 1.3;'"
              v-html="highlightMatch(node.short_name || node.name, searchQuery)"
            />
          </div>
        </div>

        <!-- Детали подразделения -->
        <div class="d-flex pa-4 dept-details gap-4" style="font-size: 12px; flex-grow: 1;">
          <!-- Сотрудники отделения (employee_count с бэка — все, включая руководителя, если он в отделении) -->
          <div class="flex-1 d-flex flex-column align-start">
            <span class="text-caption text-medium-emphasis mb-2 font-weight-medium">Сотрудники</span>
            <span
              class="custom-chip text-body-2 py-1 px-3 rounded-pill border text-medium-emphasis font-weight-medium bg-light"
              v-html="highlightMatch(`${employeeCount} сотрудников`, searchQuery)"
            />
          </div>

          <!-- Руководитель -->
          <div class="flex-1 d-flex flex-column align-start ms-4">
            <span class="text-caption text-medium-emphasis mb-2 font-weight-medium">Руководитель</span>
            <div
              v-if="manager"
              class="d-flex align-center gap-2 py-0.5 px-1 rounded w-100"
            >
              <VAvatar size="22" class="border">
                <VImg :src="getAvatarUrl(manager)" />
              </VAvatar>
              <span
                class="text-truncate text-medium-emphasis font-weight-bold manager-name-text"
                style="font-size: 12px;"
                v-html="highlightMatch((manager.first_name || manager.last_name) ? `${manager.first_name || ''} ${manager.last_name || ''}`.trim() : '', searchQuery)"
              />
            </div>
            <div class="text-caption text-error font-weight-medium" v-else>
              Не назначен
            </div>
          </div>
        </div>

        <!-- Нижняя кнопка-плашка -->
        <div
          v-if="hasChildren"
          class="bottom-action-btn d-flex align-center justify-center py-2"
          :class="{ 'expanded': isExpanded }"
          @click.stop="$emit('toggle-expand', node.id)"
        >
          <span class="text-caption font-weight-bold mr-1">Подразделений: {{ node.children?.length || 0 }}</span>
          <VIcon :icon="isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'" size="16" />
        </div>
        <div
          v-else
          class="bottom-action-btn disabled d-flex align-center justify-center py-2 text-medium-emphasis"
        >
          <span class="text-caption font-weight-medium">Нет отделов в подчинении</span>
        </div>
      </div>
    </div>

    <div
      v-if="hasChildren && isExpanded"
      class="org-node__children"
      :style="{
        left: `${childrenOffsetLeft}px`,
        top: `calc(100% + ${ORG_LEVEL_VERTICAL_GAP}px)`,
      }"
    >
      <StructureTreeNode
        v-for="sub in node.children"
        :key="sub.id"
        :node="sub"
        :selected-dept-id="selectedDeptId"
        :expanded-ids="expandedIds"
        @select="(id, name) => $emit('select', id, name)"
        @toggle-expand="id => $emit('toggle-expand', id)"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.org-node {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  flex-shrink: 0;
  width: 380px;
}

.org-node__card-wrap {
  position: relative;
  z-index: 2;
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 0;
}

.org-node__children {
  position: absolute;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 24px;
  z-index: 1;
}
</style>
